import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Title, useNotify, usePermissions } from "react-admin";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";

import { EMPLOYEE_TYPE } from "@/constants";
import { StaffCalendarLegend } from "@/containers/staff/shared/StaffCalendarLegend";
import { resolveStaffDashboardMonth } from "@/containers/staff/staff/staff-show-helpers";
import { callApi } from "@/dataprovider/misc-apis";
import type { PermissionRecord } from "@/types/permissions";
import { formatIsoMonth } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";

import { LeaveDayDrawer } from "./components/LeaveDayDrawer";
import { OfficeLeaveMonthGrid } from "./components/OfficeLeaveMonthGrid";
import {
  buildLeaveEmployeeRows,
  filterLeaveCalendarForViewer,
  filterLeaveEmployeeRows,
  type LeaveDayDrilldown,
  type OfficeLeaveCalendarEmployee,
  type OfficeLeaveCalendarEntry,
  type OfficeLeaveCalendarResponse,
} from "./leave-calendar-helpers";

/** Sentinel for department filter — matches API ALL handling. */
const DEPARTMENT_ALL = "ALL";

/**
 * Office leave month grid for portal + office staff
 * (myAttendance.view or employees.view) — table UX like department attendance.
 */
export default function LeaveCalendarPage() {
  const notify = useNotify();
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const { permissions } = usePermissions<PermissionRecord>();
  const canOfficeView = hasPermission(permissions, "employees.view");

  const [month, setMonth] = useState(() => resolveStaffDashboardMonth(null));
  const [type, setType] = useState<string>(DEPARTMENT_ALL);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<Record<string, OfficeLeaveCalendarEntry[]>>({});
  const [employees, setEmployees] = useState<OfficeLeaveCalendarEmployee[]>([]);
  const [linkedEmployeeId, setLinkedEmployeeId] = useState<number | null>(null);
  const [nameQuery, setNameQuery] = useState("");
  const [drilldown, setDrilldown] = useState<LeaveDayDrilldown | null>(null);
  const fetchGenerationRef = useRef(0);

  const fetchCalendar = useCallback(async () => {
    const generation = ++fetchGenerationRef.current;
    setLoading(true);
    try {
      const { data: response } = await callApi({
        location: "employeeLeaveRequests/office-calendar",
        data: { month, type },
        method: "GET",
      });
      if (generation !== fetchGenerationRef.current) return;
      const body = response as OfficeLeaveCalendarResponse;
      setDays(body?.days && typeof body.days === "object" ? body.days : {});
      setEmployees(Array.isArray(body?.employees) ? body.employees : []);
      setLinkedEmployeeId(
        body?.viewerEmployeeId != null && Number.isFinite(body.viewerEmployeeId)
          ? body.viewerEmployeeId
          : null
      );
    } catch {
      if (generation !== fetchGenerationRef.current) return;
      notifyRef.current("Failed to load leave calendar", { type: "error" });
      setDays({});
      setEmployees([]);
      setLinkedEmployeeId(null);
    } finally {
      if (generation === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [month, type]);

  useEffect(() => {
    void fetchCalendar();
  }, [fetchCalendar]);

  const rows = useMemo(() => {
    const built = buildLeaveEmployeeRows(days, employees);
    return filterLeaveCalendarForViewer(built, days, {
      canOfficeView,
      linkedEmployeeId,
    }).rows;
  }, [days, employees, canOfficeView, linkedEmployeeId]);
  const visibleRows = useMemo(() => filterLeaveEmployeeRows(rows, nameQuery), [rows, nameQuery]);
  const withLeaveCount = useMemo(
    () => visibleRows.filter((r) => r.summary.leaveDays > 0).length,
    [visibleRows]
  );

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pb: 3, width: "100%", minWidth: 0 }}>
      <Title title="Leave calendar" />
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 0.5 }}>
            Leave calendar
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canOfficeView
              ? "Pending and approved leave for the office (staff × day)"
              : "Team leave planning — approved leave for everyone; your pending requests appear on your row only"}
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            label="Month"
            type="month"
            size="small"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value || formatIsoMonth(dayjs()));
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", sm: 180 } }}
          />
          <FormControl size="small" sx={{ width: { xs: "100%", sm: 180 } }}>
            <InputLabel id="leave-calendar-type-label">Department</InputLabel>
            <Select
              labelId="leave-calendar-type-label"
              value={type}
              onChange={(e) => setType(e.target.value)}
              label="Department"
            >
              <MenuItem value={DEPARTMENT_ALL}>All</MenuItem>
              {Object.entries(EMPLOYEE_TYPE).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Search staff"
            size="small"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            sx={{ width: { xs: "100%", sm: 200 } }}
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton
                aria-label="Refresh leave calendar"
                onClick={() => void fetchCalendar()}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <StaffCalendarLegend
          mode="leave"
          showPendingLeave={canOfficeView}
          portalNote={
            canOfficeView
              ? undefined
              : "Pending leave is shown on your row only; colleagues' pending requests are hidden."
          }
        />

        <Typography variant="caption" color="text.secondary">
          {visibleRows.length} staff
          {withLeaveCount > 0 ? ` · ${withLeaveCount} with leave` : ""}
          {nameQuery.trim() && rows.length !== visibleRows.length ? ` (of ${rows.length})` : ""}
          {canOfficeView
            ? " · Lp pending · A approved · summary columns L / A / P = leave / approved / pending days"
            : " · A approved · your row may show Lp for your pending leave · summary L / A / P = leave / approved / pending days"}
        </Typography>

        <Box sx={{ position: "relative" }}>
          {loading ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.7),
                zIndex: 7,
                borderRadius: 1,
              }}
            >
              <CircularProgress size={36} />
            </Box>
          ) : null}
          <OfficeLeaveMonthGrid
            month={month}
            rows={visibleRows}
            loading={loading}
            canOfficeView={canOfficeView}
            onDayClick={setDrilldown}
          />
        </Box>
      </Stack>

      <LeaveDayDrawer
        open={drilldown != null}
        drilldown={drilldown}
        month={month}
        onClose={() => setDrilldown(null)}
        canOfficeView={canOfficeView}
        linkedEmployeeId={linkedEmployeeId}
      />
    </Box>
  );
}
